/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { route } from 'preact-router';
import kUtils from 'kurento-utils';
import { LogLevel } from '@bugfender/sdk';
import * as Logger from './logger';
import { safeSetMediaPlaybackState } from './media-session';
import { getUserDetails } from '../api/pros';
// eslint-disable-next-line import/no-cycle
import { generateJID } from './index';
// eslint-disable-next-line import/no-cycle
import { routes } from '../components/app';

const __FILE__ = 'connection.js';

const users = {};

const REDACT_CHAT_MSG_BODY_REGEXP = /<body>[^<]+<\/body>/g;
const REDACT_CHAT_MSG_BODY_REPLACE_STRING = '...';

export const checkUserAndGet = async (id, _userAuth) => {
  let usr;
  if (users[id]) {
    usr = users[id];
  } else {
    usr = await getUserDetails(id, _userAuth) || {};
    users[id] = usr;
  }

  return usr;
};

export const isCurrentUserRoute = (url, userId) => {
  const currentUserId = url.split('/').pop().split('#')[0];
  return currentUserId === `${userId}`;
};

const mergeIncomingMessageWithHistory = (history, incomingMsg) => {
  const acc = {
    mergedHistory: [],
    incomingMsgWasInHistory: false,
  };

  const checkedHistory = history.reduce((prev, current) => {
    if (current.id === incomingMsg.id) {
      prev.mergedHistory.push({
        ...incomingMsg,
        ...current,
      });
      prev.incomingMsgWasInHistory = true;
    } else {
      prev.mergedHistory.push(current);
    }

    return prev;
  }, acc);

  return checkedHistory.incomingMsgWasInHistory ? checkedHistory.mergedHistory : [...history, incomingMsg];
};

export const setMessages = (id, msg, isMy, prevState) => {
  const _id = id.split('/')[0];
  const currentChat = prevState.chats[_id];
  const chat = currentChat
    ? currentChat.chat
      ? mergeIncomingMessageWithHistory(currentChat.chat, { ...msg, isMy })
      : [{ ...msg, isMy }]
    : [{ ...msg, isMy }];
  chat.sort((a, b) => a.timestamp - b.timestamp);
  return ({
    chats: {
      ...prevState.chats,
      [_id]: {
        chat,
        isDisplayed: isMy,
        mesId: isMy ? '' : msg.id,
        thread: isMy ? '' : msg.thread,
        lastMessage: isMy ? msg.id : chat.lastMessage,
      },
    },
  });
};

const mergeMessageHistory = (storedHistory, receivedHistory) => {
  const receivedMessageIds = receivedHistory.reduce((acc, current) => {
    acc.add(current.id);
    return acc;
  }, new Set());

  return [
    ...storedHistory.filter((msg) => !receivedMessageIds.has(msg.id)),
    ...receivedHistory,
  ].sort((a, b) => a.timestamp - b.timestamp);
};

export const setMessageHistory = (id, msgArr, count, prevState) => {
  const _id = id.split('/')[0];
  const chat = prevState.chats[_id]
    ? prevState.chats[_id].chat
      ? mergeMessageHistory(prevState.chats[_id].chat, msgArr)
      : [...msgArr]
    : [...msgArr];
  return ({
    chats: {
      ...prevState.chats,
      [_id]: {
        chat,
        isDisplayed: true,
        lastMessage: msgArr[msgArr.length - 1].id,
      },
    },
    received: count,
  });
};
export const setUser = (user, prevState) => ({
  users: {
    ...prevState.users,
    [user.id]: { ...user },
  },
});
export const clearUserChat = (userId, prevState) => {
  const userJID = generateJID(userId, true);
  const chats = { ...prevState.chats };
  delete chats[userJID];
  return ({ chats: { ...chats }, received: -1 });
};

export const processServerMsg = (msg) => {
  const __METHOD__ = 'con-helpers::processServerMsg';

  const outerMessageRecipient = msg.getAttribute('to');
  const from = msg.getAttribute('from');
  const outerMessageType = msg.getAttribute('type');
  const isChatMessage = !!(outerMessageType && outerMessageType.toLowerCase() === 'chat');
  const isCarbonCopy = outerMessageRecipient.indexOf(from) === 0;

  const message = (isChatMessage && isCarbonCopy) ? msg.getElementsByTagName('message')[0] : msg;
  if (!message) {
    const msgContents = (msg.tree && typeof msg.tree === 'function') ? msg.tree().outerHTML : '<unavailable>';
    const errMsg = `Received an XMPP message that looks like a carbon copy but does not contain an inner message: ${msgContents}`;

    Logger.logXMPPReceive(__FILE__, __METHOD__, errMsg, LogLevel.Error, msg);
    throw new Error(errMsg);
  }

  const to = (isChatMessage && isCarbonCopy)
    ? message.getAttribute('to')
    : outerMessageRecipient;
  const type = message.getAttribute('type');
  const timestamp = message.getAttribute('timestamp');
  const id = message.getAttribute('id');
  const inCall = message.getAttribute('inCall');
  const elems = message.getElementsByTagName('body');
  const thread = message.getElementsByTagName('thread');
  const vcxepElems = message.getElementsByTagName('vcxep');
  return {
    to, from, type, id, elems, thread, vcxepElems, inCall, timestamp, isMe: isCarbonCopy,
  };
};

export const processChatMsg = async (from, _userId, _userAuth, changeUnreadNum, message, createNotification, audio, calleeId, isMe) => {
  const [fromId] = from.split('@');

  // Show notification
  if (!document.hasFocus() && !isMe) {
    createNotification(`New message from ${message.senderName}`, message.text, 10000, () => {
      route(`${routes.USER_FOR_COMP + fromId}#chat`);
    });
  }
  let _usr = { id: fromId };
  if (!isMe && `${calleeId}` !== `${fromId}`) {
    // get user info for unread messages
    if (users[fromId]) {
      _usr = users[fromId];
    } else {
      _usr = await getUserDetails(fromId, _userAuth) || {};
      users[fromId] = _usr;
    }
  }

  if (!isMe) {
    if (audio) {
      audio.play();
      safeSetMediaPlaybackState('none');
    }
  }
  return _usr;
};

export const redactChatMessageBody = (message) => {
  const type = message.getAttribute('type');
  const elems = message.getElementsByTagName('body');
  const stanzaType = message.localName.toLowerCase();

  if (stanzaType === 'message' && (type === 'chat' || type === 'error') && elems.length > 0) {
    return message.outerHTML.replace(REDACT_CHAT_MSG_BODY_REGEXP, REDACT_CHAT_MSG_BODY_REPLACE_STRING);
  }

  return message.outerHTML;
};

export const processCallMsg = (body) => {
  const type = body.getAttribute('type');
  const callId = body.getAttribute('callid');
  const avtime = body.getAttribute('avtime');
  const bodyInner = body.innerHTML;
  return {
    type, callId, avtime, bodyInner,
  };
};

export const isCallRequestMsg = (stanza) => {
  if (!stanza.tree || typeof stanza.tree !== 'function') {
    return [false, null];
  }

  const msg = stanza.tree();

  if (msg.getAttribute('type') !== 'vcxep') {
    return [false, null];
  }

  const vcxepElems = msg.getElementsByTagName('vcxep');
  if (!(vcxepElems instanceof HTMLCollection) || vcxepElems.length === 0) {
    return [false, null];
  }

  const body = vcxepElems[0];
  if (body.getAttribute('type') === 'request') {
    return [true, body.getAttribute('callid')];
  }

  return [false, null];
};

export const encodeXMPPmessage = (msg) => {
  let text = msg.replace(/&quot;/g, '"');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&apos;/g, "'");
  const message = JSON.parse(text);
  return message;
};

const connectAudioStreamToAudioPlayback = (options, playableAudioStream) => {
  const __METHOD__ = 'con-helpers::connectAudioStreamToAudioPlayback';

  if (options.remoteAudio) {
    if (options.remoteAudio.srcObject && options.remoteAudio.srcObject === playableAudioStream) {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        'Incoming audio stream is now live but appears to have already been connected to audio playback. So, nothing to do',
      );
      return;
    }

    Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming audio stream is now live - connecting to audio playback');

    options.remoteAudio.srcObject = playableAudioStream;
    options.remoteAudio.load();
    options.remoteAudio.play().catch((e) => {
      safeSetMediaPlaybackState('none');

      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        `Failed to play the incoming audio stream. An error occurred: ${e.name || 'unknown error'} with message '${e.message}'`,
        LogLevel.Warning,
      );
    });
    safeSetMediaPlaybackState('none');
  } else {
    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'Incoming audio stream appeared but no HTML audio element available to connect the stream to',
      LogLevel.Warning,
    );
  }
};

const connectVideoStreamToVideoPlayback = (options, playableVideoStream) => {
  const __METHOD__ = 'con-helpers::connectVideoStreamToVideoPlayback';

  if (!(playableVideoStream instanceof MediaStream)) {
    Logger.logWebRTC(__FILE__, __METHOD__, 'The supplied stream is not a valid MediaStream', LogLevel.Error, playableVideoStream);
    return;
  }

  if (options.remoteVideo) {
    if (options.remoteVideo.srcObject && options.remoteVideo.srcObject === playableVideoStream) {
      Logger.logWebRTC(
        __FILE__,
        __METHOD__,
        'Incoming video stream is now live but appears to have already been connected to video playback. So, nothing to do',
      );
      return;
    }

    Logger.logWebRTC(__FILE__, __METHOD__, 'Incoming video stream is now live - connecting to video playback');

    if (options.remoteVideo.srcObject) {
      options.remoteVideo.pause();
    }
    options.remoteVideo.srcObject = playableVideoStream;
    options.remoteVideo.muted = true;
    options.remoteVideo.volume = 0;
    options.remoteVideo.load();
    options.remoteVideo.play().catch((e) => {
      if (e instanceof DOMException && e.name === 'AbortError') {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          'A new incoming video stream replaced an existing video stream',
          LogLevel.Warning,
        );
      } else {
        Logger.logWebRTC(
          __FILE__,
          __METHOD__,
          `Failed to play the incoming video stream. An error occurred: ${e.name || 'unknown error'} with message '${e.message}'`,
          LogLevel.Warning,
        );
      }
    });
  } else {
    Logger.logWebRTC(
      __FILE__,
      __METHOD__,
      'Incoming video stream appeared but no HTML video element available to connect the stream to',
      LogLevel.Warning,
    );
  }
};

// eslint-disable-next-line no-unused-vars
export const createPeerConnection = (from, to, msgGenSend = () => {}, options = {}, cInfo, callback, canPlayAudioTrack, shallMuteOutgoingAudio, canPlayIncomingVideo) => {
  const __METHOD__ = 'con-helpers::createPeerConnection';

  let webRtcPeer;

  const peerConnectionCreatedCallback = (err) => {
    const __CALLBACK_METHOD__ = 'createPeerConnection::peerConnectionCreatedCallback';
    const __CALLBACK_METHOD_ON_TRACK__ = 'createPeerConnection::peerConnectionCreatedCallback::ontrackCallback';
    const __CALLBACK_METHOD_ON_ADD_TRACK__ = 'createPeerConnection::peerConnectionCreatedCallback::ontrack::onaddtrackCallback';
    const __CALLBACK_METHOD_ON_REMOVE_TRACK__ = 'createPeerConnection::peerConnectionCreatedCallback::ontrack::onremovetrackCallback';
    const __CALLBACK_METHOD_ON_MUTE__ = 'createPeerConnection::peerConnectionCreatedCallback::onmuteCallback';
    const __CALLBACK_METHOD_ON_UNMUTE__ = 'createPeerConnection::peerConnectionCreatedCallback::onunmuteCallback';

    if (err) {
      Logger.logWebRTC(
        __FILE__,
        __CALLBACK_METHOD__,
        'Failed to create a peer connection',
        LogLevel.Warning,
        err,
      ); // & setCallState(NO_CALL);

      callback && callback(err);

      return;
    }

    webRtcPeer.peerConnection.ontrack = ({ transceiver, streams, track }) => {
      Logger.logWebRTC(
        __FILE__,
        __CALLBACK_METHOD_ON_TRACK__,
        `A track has been added to peer connection. Tranceiver mid: ${transceiver.mid}, id: ${track.id}, kind: ${track.kind}, muted: ${track.muted}, enabled: ${track.enabled}`,
      );

      const stream = [streams];

      if (stream) {
        stream.onaddtrack = () => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_ON_ADD_TRACK__,
            'New track has been added to the stream',
            LogLevel.Info,
            stream,
          );
        };
        stream.onremovetrack = () => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_ON_REMOVE_TRACK__,
            'A track has been removed from the stream',
            LogLevel.Info,
            stream,
          );
        };
      }
      transceiver.receiver.track.onmute = () => {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_ON_MUTE__,
          `A track of kind '${transceiver.receiver.track.kind}' labeled '${transceiver.receiver.track.label}' has stopped sending data`,
        );
      };
      transceiver.receiver.track.onunmute = () => {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_ON_UNMUTE__,
          `A track of kind '${transceiver.receiver.track.kind}' labeled '${transceiver.receiver.track.label}' has started sending data`,
        );

        if (transceiver.receiver.track.kind === 'video') {
          if (canPlayIncomingVideo && typeof canPlayIncomingVideo === 'function') {
            if (canPlayIncomingVideo()) {
              const playableVideoStream = streams.find(
                (trackStream) => trackStream.getVideoTracks().length && trackStream.active,
              );

              if (playableVideoStream) {
                connectVideoStreamToVideoPlayback(options, playableVideoStream);
              }
            }
          }
        } else if (transceiver.receiver.track.kind === 'audio') {
          if (canPlayAudioTrack && typeof canPlayAudioTrack === 'function') {
            if (canPlayAudioTrack(to)) {
              const playableAudioStream = streams.find(
                (trackStream) => !trackStream.getVideoTracks().length && trackStream.active,
              );

              if (playableAudioStream) {
                connectAudioStreamToAudioPlayback(options, playableAudioStream);
              }
            }
          }
        }
      };
    };
    if (shallMuteOutgoingAudio && typeof shallMuteOutgoingAudio === 'function' && shallMuteOutgoingAudio()) {
      webRtcPeer.peerConnection.getSenders().forEach((element) => {
        element.track && element.track.kind === 'audio' && (
          element.track.enabled = false
        );
      });
    }
  };

  Logger.logWithTag(
    __FILE__,
    __METHOD__,
    Logger.TAG_WEBRTC,
    'Creating a peer connection with media constraints: ',
    LogLevel.Info,
    options.mediaConstraints,
  );

  webRtcPeer = kUtils.WebRtcPeer.WebRtcPeerSendrecv(options, peerConnectionCreatedCallback);
  return webRtcPeer;
};

export const sendInCallOfferData = (from, to, webRtcPeer, msgGenSend = () => {}, cInfo) => {
  const __METHOD__ = 'sendInCallOfferData';

  Logger.logWebRTC(
    __FILE__,
    __METHOD__,
    `Send incall offer data at ${new Date().toISOString()}`,
  );

  webRtcPeer.generateOffer((err, offerSdp) => {
    err && console.log(err); // & setCallState(NO_CALL);
    !err && msgGenSend(from, to, 'vcxep', 'vcxep', { type: 'offerData', callid: cInfo.callId }, offerSdp, true);
  });
  return webRtcPeer;
};

export const sendAnswerData = (from, to, sdpOffer, msgGenSend = () => {}, options = {}, cInfo, callback, canPlayAudioTrack, shallMuteOutgoingAudio, canPlayIncomingVideo) => {
  const __METHOD__ = 'sendAnswerData';

  let webRtcPeer;

  const onPeerConnectionCreated = (err) => {
    const __CALLBACK_METHOD__ = 'sendAnswerData::onPeerConnectionCreated';
    const __CALLBACK_METHOD_ON_TRACK__ = 'sendAnswerData::onPeerConnectionCreated::ontrackCallback';
    const __CALLBACK_METHOD_ON_ADD_TRACK__ = 'sendAnswerData::onPeerConnectionCreated::ontrack::onaddtrackCallback';
    const __CALLBACK_METHOD_ON_REMOVE_TRACK__ = 'sendAnswerData::onPeerConnectionCreated::ontrack::onremovetrackCallback';
    const __CALLBACK_METHOD_ON_MUTE__ = 'sendAnswerData::onPeerConnectionCreated::onmuteCallback';
    const __CALLBACK_METHOD_ON_UNMUTE__ = 'sendAnswerData::onPeerConnectionCreated::onunmuteCallback';
    const __CALLBACK_METHOD_PROCESS_OFFER__ = 'sendAnswerData::onPeerConnectionCreated::processOfferCallback';

    if (err) {
      Logger.logWebRTC(
        __FILE__,
        __CALLBACK_METHOD__,
        'Failed to create a peer connection',
        LogLevel.Error,
        err,
      ); // & setCallState(NO_CALL);

      callback(err);

      return;
    }

    webRtcPeer.peerConnection.ontrack = ({ transceiver, streams, track }) => {
      Logger.logWebRTC(
        __FILE__,
        __CALLBACK_METHOD_ON_TRACK__,
        `A track has been added to peer connection. Tranceiver mid: ${transceiver.mid}, id: ${track.id}, kind: ${track.kind}, muted: ${track.muted}, enabled: ${track.enabled}`,
      );

      const [stream] = streams;

      if (stream) {
        stream.onaddtrack = () => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_ON_ADD_TRACK__,
            'New track has been added to the stream',
            LogLevel.Info,
            stream,
          );
        };
        stream.onremovetrack = () => {
          Logger.logWebRTC(
            __FILE__,
            __CALLBACK_METHOD_ON_REMOVE_TRACK__,
            'A track has been removed from the stream',
            LogLevel.Info,
            stream,
          );
        };
      }
      transceiver.receiver.track.onmute = () => {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_ON_MUTE__,
          `A track of kind '${transceiver.receiver.track.kind}' labeled '${transceiver.receiver.track.label}' has stopped sending data`,
        );
      };
      transceiver.receiver.track.onunmute = () => {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_ON_UNMUTE__,
          `A track of kind '${transceiver.receiver.track.kind}' labeled '${transceiver.receiver.track.label}' has started sending data`,
        );

        if (transceiver.receiver.track.kind === 'video') {
          if (canPlayIncomingVideo && typeof canPlayIncomingVideo === 'function') {
            if (canPlayIncomingVideo()) {
              const playableVideoStream = streams.find(
                (trackStream) => trackStream.getVideoTracks().length && trackStream.active,
              );

              if (playableVideoStream) {
                connectVideoStreamToVideoPlayback(options, playableVideoStream);
              }
            }
          }
        } else if (transceiver.receiver.track.kind === 'audio') {
          if (canPlayAudioTrack && typeof canPlayAudioTrack === 'function') {
            if (canPlayAudioTrack()) {
              const playableAudioStream = streams.find(
                (trackStream) => !trackStream.getVideoTracks().length && trackStream.active,
              );

              if (playableAudioStream) {
                connectAudioStreamToAudioPlayback(options, playableAudioStream);
              }
            }
          }
        }
      };
    };

    webRtcPeer.processOffer(sdpOffer, (offerProcessingErr, sdpAnswer) => {
      if (!offerProcessingErr) {
        msgGenSend(from, to, 'vcxep', 'vcxep', { type: 'answerData', callid: cInfo.callId }, sdpAnswer, true);
        if (shallMuteOutgoingAudio && typeof shallMuteOutgoingAudio === 'function' && shallMuteOutgoingAudio()) {
          webRtcPeer.peerConnection.getSenders().forEach((element) => {
            element.track && element.track.kind === 'audio' && (
              element.track.enabled = false
            );
          });
        }
      } else {
        Logger.logWebRTC(
          __FILE__,
          __CALLBACK_METHOD_PROCESS_OFFER__,
          'Failed to process the SDP offer',
          LogLevel.Error,
          offerProcessingErr,
        );
      }

      callback();
    });
  };

  Logger.logWithTag(
    __FILE__,
    __METHOD__,
    Logger.TAG_WEBRTC,
    'Creating a peer connection with media constraints: ',
    LogLevel.Info,
    options.mediaConstraints,
  );

  webRtcPeer = kUtils.WebRtcPeer.WebRtcPeerSendrecv(options, onPeerConnectionCreated);
  return webRtcPeer;
};

export const sendInCallAnswerData = (from, to, webRtcPeer, sdpOffer, msgGenSend = () => {}, cInfo, callback) => {
  const __METHOD__ = 'sendInCallAnswerData';
  const __CALLBACK_METHOD_PROCESS_OFFER__ = 'sendInCallAnswerData::processOfferCallback';

  Logger.logWebRTC(
    __FILE__,
    __METHOD__,
    `Send incall answer data at ${new Date().toISOString()} for the received SDP offer`,
    LogLevel.Info,
    sdpOffer,
  );

  webRtcPeer.processOffer(sdpOffer, (err, sdpAnswer) => {
    err && Logger.logWebRTC(
      __FILE__,
      __CALLBACK_METHOD_PROCESS_OFFER__,
      'Failed to process the SDP offer',
      LogLevel.Error,
      err,
    ); // & setCallState(NO_CALL);
    !err && msgGenSend(from, to, 'vcxep', 'vcxep', { type: 'answerData', callid: cInfo.callId }, sdpAnswer, true);

    if (!err && callback && typeof callback === 'function') {
      callback(webRtcPeer);
    }
  });
  return webRtcPeer;
};

export const onIceCandidate = (from, to, callId, msgGenSend = () => {}) => (candidate) => candidateData(from, to, callId, candidate, msgGenSend);
function candidateData(from, to, callId, candidate, msgGenSend) {
  const __METHOD__ = 'candidateData';

  const data = {
    sdp: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
  };
  Logger.logWebRTC(
    __FILE__,
    __METHOD__,
    'sending a local ICE candidate',
    LogLevel.Info,
    data,
  );

  msgGenSend(from, to, 'vcxep', 'vcxep', { type: 'candidateData', callid: callId }, JSON.stringify([data]), true);
}

// export const videoCapture = (isDisabled, msgGenSend = () => {}, props) => {
//   const { callInfo = {} } = props.comModal;
//   const address = prepareFromTo(props);
//   const type = isDisabled ? 'dis_video' : 'en_video';
//   msgGenSend(address, 'vcxep', 'vcxep', { type, callid: callInfo.callId });
// };

export const getMediaConstraints = () => navigator.mediaDevices.enumerateDevices().then((deviceInfos) => {
  // const audio = deviceInfos.some((d) => d.kind === 'audioinput');
  const __METHOD__ = 'con-helpers::getMediaConstraints';

  const inputDevices = deviceInfos.filter((d) => d.kind === 'audioinput');

  Logger.logWithTag(
    __FILE__,
    __METHOD__,
    Logger.TAG_WEBRTC,
    'Detected audio devices: ',
    LogLevel.Debug,
    inputDevices,
    true,
  );

  if (inputDevices && inputDevices.length > 0) {
    const preferredDeviceId = inputDevices.reduce((prevValue, currentValue, currentIndex, devicesArray) => {
      if ((!prevValue || prevValue !== 'communications') && currentValue.deviceId === 'communications') {
        // Default communications device found - override any previuosly detected preferred devices.
        return currentValue.deviceId;
      }

      if (!prevValue && currentValue.deviceId === 'default') {
        // Default audio input device found - assume it as the preferred device so far.
        return currentValue.deviceId;
      }

      if (!prevValue && currentIndex === devicesArray.length - 1) {
        // We've iterated over all the available device and did not find a preferred one -
        // use the first available as a sensible default.
        return devicesArray[0].deviceId;
      }

      return prevValue;
    }, null);

    Logger.logWithTag(
      __FILE__,
      __METHOD__,
      Logger.TAG_WEBRTC,
      `Requesting an audio input device with ID: ${preferredDeviceId}`,
      LogLevel.Info,
      undefined,
      false,
    );

    if (preferredDeviceId !== '') {
      return {
        audio: {
          echoCancellation: true,
          deviceId: preferredDeviceId,
        },
        video: false,
      };
    }

    return {
      audio: {
        echoCancellation: true,
      },
      video: false,
    };
  }

  Logger.logWebRTC(
    __FILE__,
    __METHOD__,
    'No audio input devices found; calls functionality will not be available until an audio input device is plugged',
    LogLevel.Warning,
    undefined,
  );

  return { audio: false, video: false };
}).catch((err) => {
  const __CALLBACK_METHOD_ENUMERATE_DEVICES__ = 'getMediaConstraints::enumerateDevicesCallback';

  Logger.logWebRTC(
    __FILE__,
    __CALLBACK_METHOD_ENUMERATE_DEVICES__,
    'Failed to enumarate media devices',
    LogLevel.Error,
    err,
  );
  return { audio: true, video: false };
});

export const trackStop = (track) => {
  track.enabled = false;
  track.stop();
};



// WEBPACK FOOTER //
// ./utils/con-helpers.js